import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminStatCard } from "./AdminStatCard";
import { toast } from "sonner";
import { Users, UserCheck, Plus, X, Save, Trash2, Pencil, Phone, Mail, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPunonjesit = () => {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form, setForm] = useState({
    username: "", password: "", name: "", position: "", department: "",
    start_date: "", phone: "", emergency_phone: "", salary: ""
  });

  useEffect(() => { loadEmployees(); loadStats(); }, []);

  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch (e) { console.error(e); }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/employees/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const openModal = (emp = null) => {
    if (emp) {
      setEditingEmployee(emp);
      setForm({
        username: emp.username, password: "", name: emp.name, position: emp.position || "",
        department: emp.department || "", start_date: emp.start_date || "",
        phone: emp.phone || "", emergency_phone: emp.emergency_phone || "",
        salary: emp.salary || ""
      });
    } else {
      setEditingEmployee(null);
      setForm({ username: "", password: "", name: "", position: "", department: "", start_date: "", phone: "", emergency_phone: "", salary: "" });
    }
    setShowModal(true);
  };

  const saveEmployee = async () => {
    if (!form.username || !form.name) { toast.error("Username dhe Emri janë të detyrueshëm"); return; }
    if (!editingEmployee && !form.password) { toast.error("Password-i është i detyrueshëm për punonjës të ri"); return; }
    try {
      const token = localStorage.getItem("adminToken");
      const payload = { ...form, salary: form.salary ? parseFloat(form.salary) : 0 };
      if (editingEmployee) {
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/admin/employees/${editingEmployee.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Punonjësi u përditësua");
      } else {
        await axios.post(`${API}/admin/employees`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Punonjësi u shtua");
      }
      setShowModal(false); loadEmployees(); loadStats();
    } catch (e) {
      const msg = e.response?.data?.detail || "Gabim";
      toast.error(msg); console.error(e);
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Jeni të sigurt?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/employees/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Punonjësi u fshi"); loadEmployees(); loadStats();
    } catch (e) { toast.error("Gabim"); }
  };

  const toggleActive = async (emp) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/employees/${emp.id}`, { active: !emp.active }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(emp.active ? "Punonjësi u çaktivizua" : "Punonjësi u aktivizua");
      loadEmployees(); loadStats();
    } catch (e) { toast.error("Gabim"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Punonjësit & Detyrat</h2>
        <Button onClick={() => openModal()} className="bg-yellow-600 hover:bg-yellow-500" data-testid="add-employee-btn">
          <Plus className="h-4 w-4 mr-2" /> Shto Punonjës
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <AdminStatCard value={stats.active} label="Punonjës aktivë" color="green" icon={<UserCheck className="h-5 w-5" />} />
        <AdminStatCard value={stats.total} label="Gjithsej punonjës" color="yellow" icon={<Users className="h-5 w-5" />} />
      </div>

      {employees.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Nuk ka punonjës të regjistruar</p>
          <p className="text-sm mt-1">Shtoni punonjësin e parë me butonin sipër</p>
        </div>
      ) : (
        <div className="space-y-3">
          {employees.map((emp) => (
            <div key={emp.id} className={`bg-zinc-900 border rounded-xl p-4 hover:border-gray-700 transition-colors ${!emp.active ? "opacity-60" : ""} ${emp.active ? "border-gray-800" : "border-red-600/20"}`} data-testid={`employee-${emp.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{emp.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${emp.active ? "bg-green-600/20 text-green-400" : "bg-red-600/20 text-red-400"}`}>
                      {emp.active ? "Aktiv" : "Joaktiv"}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm">@{emp.username}</p>
                  <div className="flex gap-4 text-gray-500 text-xs mt-1">
                    {emp.position && <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{emp.position}</span>}
                    {emp.department && <span>{emp.department}</span>}
                    {emp.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{emp.phone}</span>}
                  </div>
                  {emp.salary > 0 && <p className="text-yellow-600 text-xs mt-1">Paga: €{emp.salary}/muaj</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(emp)} className={emp.active ? "text-red-500 hover:text-red-400" : "text-green-500 hover:text-green-400"}>
                    <UserCheck className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openModal(emp)} className="text-gray-400 hover:text-white">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteEmployee(emp.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{editingEmployee ? "Ndrysho Punonjësin" : "Shto Punonjës"}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-gray-300">Username *</Label>
                <Input value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" disabled={!!editingEmployee} data-testid="emp-username" />
              </div>
              <div>
                <Label className="text-gray-300">{editingEmployee ? "Password i ri (lini bosh nëse nuk ndryshoni)" : "Password *"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" data-testid="emp-password" />
              </div>
              <div>
                <Label className="text-gray-300">Emri i plotë *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" data-testid="emp-name" />
              </div>
              <div>
                <Label className="text-gray-300">Pozicioni</Label>
                <Input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" placeholder="p.sh. Menaxher Operacionesh" />
              </div>
              <div>
                <Label className="text-gray-300">Departamenti</Label>
                <Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" placeholder="p.sh. Operacionet" />
              </div>
              <div>
                <Label className="text-gray-300">Data e fillimit</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Telefoni</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Telefon emergjence</Label>
                <Input value={form.emergency_phone} onChange={(e) => setForm({...form, emergency_phone: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-300">Paga mujore (€)</Label>
                <Input type="number" value={form.salary} onChange={(e) => setForm({...form, salary: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Anulo</Button>
              <Button onClick={saveEmployee} className="bg-yellow-600 hover:bg-yellow-500"><Save className="h-4 w-4 mr-2" />Ruaj</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
